import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  Divider,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Slide from "@mui/material/Slide";
import { useStyles } from "../../assets/styles/Theme";
import AddToolbar from "../../components/common/AddToolbar";
import Navbar from "../../components/common/Navbar";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";

import {
  externalpostApi,
  externalgetApi,
} from "../../services/external/externalApis";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import {
  CON_STATUS_ACTIVE,
  CON_MENU_TYPE_CODE,
} from "../../utils/sys/Constants";
import Popover from "@mui/material/Popover";

import { useSelector } from "react-redux";
import { Schedule_API_1 } from "../../api/ApiModel";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

function ServerDay(props) {
  const { day, outsideCurrentMonth, highlightedDays, ...other } = props;
  const isScheduled = highlightedDays.includes(dayjs(day).format("YYYY-MM-DD"));

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={isScheduled ? "📅" : undefined}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        className={isScheduled ? "highlighted" : ""}
      />
    </Badge>
  );
}

function Schedules() {
  const navigate = useNavigate();
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, BUSINESSTYPECODE, USERNAME } =
    SYS_USER.USER_PARAM || {};
  const theme = useTheme();
  const styles = useStyles(theme);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const location = useLocation();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchOnLoad, setFetchOnLoad] = useState(true);
  const [errors, setErrors] = useState({});
  const [selectedItemDetails, setSelectedItemDetails] = useState({
    EXPIRYDATE: null,
    GUESTNAME: "",
    CONTACTNO: "",
    EMAIL: "",
    REMARK: "",
    SCHEDULEDATE: "",
    TIMEFROM: "",
    TIMETO: "",
  });
  const isEdit = location.state?.actionText === "Edit";

  const [selectedDate, setSelectedDate] = useState(null);
  const [scheduledDates, setScheduledDates] = useState([]);
  const [open, setOpen] = useState(false);
  const [meetingDetails, setMeetingDetails] = useState("");
  const [data, setData] = useState(false);
  // Function to handle date changes specifically

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear the error for the specific field when the user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Handle clear button click
  const handleClearClick = () => {
    setSelectedItemDetails({
      EXPIRYDATE: null,
      GUESTNAME: "",
      CONTACTNO: "",
      EMAIL: "",
      REMARK: "",
      SCHEDULEDATE: "",
      TIMEFROM: "",
      TIMETO: "",
    });
    setErrors({});
  };

  // Handle confirm button click and make the POST request
  const handleConfirmClick = async () => {
    // Validate required fields
    const requiredFields = [
      {
        name: "GUESTNAME",
        value: selectedItemDetails.GUESTNAME,
        label: "Guest Name",
      },
      {
        name: "CONTACTNO",
        value: selectedItemDetails.CONTACTNO,
        label: "Contact Number",
      },
      { name: "EMAIL", value: selectedItemDetails.EMAIL, label: "Email" },
      {
        name: "SCHEDULEDATE",
        value: selectedItemDetails.SCHEDULEDATE,
        label: "Scheduled Date",
      },
      {
        name: "TIMEFROM",
        value: selectedItemDetails.TIMEFROM,
        label: "Start Time",
      },
      { name: "TIMETO", value: selectedItemDetails.TIMETO, label: "End Time" },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) => !field.value
    );

    if (missingRequiredFields.length > 0) {
      const newError = {};
      missingRequiredFields.forEach((field) => {
        newError[field.name] = `${field.label} is required.`;
      });

      setErrors(newError); // Update the error state
      const missingFieldLabels = missingRequiredFields
        .map((field) => field.label)
        .join(", ");
      const errorMessage = `Please Fill All the Required Fields.`;

      setSnackbarMessage(errorMessage); // Show a snackbar message with missing fields
      setOpenErrorSnackbar(true); // Open error snackbar
      return; // Stop submission if there are validation errors
    }

    // Validate Email Format
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!validateEmail(selectedItemDetails.EMAIL)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        EMAIL: "Please enter a valid email address.",
      }));
      setSnackbarMessage("Please enter a valid email address.");
      setOpenErrorSnackbar(true);
      return; // Stop further execution if the email is invalid
    }

    // If validation is successful, proceed with the API call
    setIsLoading(true);

    const payload = {
      COMPCODE,
      LOCCODE,
      RESTCODE,
      scheduledate: dayjs(selectedItemDetails.SCHEDULEDATE).format(
        "MM-DD-YYYY"
      ),
      guestname: selectedItemDetails.GUESTNAME,
      contactno: selectedItemDetails.CONTACTNO,
      email: selectedItemDetails.EMAIL,
      timefrom: dayjs(selectedItemDetails.TIMEFROM).format("hh:mm:ss A"),
      timeto: dayjs(selectedItemDetails.TIMETO).format("hh:mm:ss A"),
      remark: selectedItemDetails.REMARK,
      sts: "A", // Assuming 'sts' as "Active"
      enuser: USERNAME, // Replace with the actual username
    };

    try {
      const responseData = await externalpostApi(Schedule_API_1, payload);
      console.log("Success:", responseData);
      setSnackbarMessage("Schedule saved successfully!");
      setOpenSnackbar(true);
      handleClearClick(); // Clear form fields after success
      fetchDetailsByDate();
    } catch (error) {
      console.error("API Error:", error);
      setSnackbarMessage("Something went wrong, please try again!");
      setOpenErrorSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const gotToView = () => {
    navigate("/VSchedule");
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenErrorSnackbar(false); // Reset the snackbar state
  };

  useEffect(() => {
    if (fetchOnLoad) {
      fetchDetailsByDate();
      setFetchOnLoad(false); // Prevent further fetches unless explicitly triggered
    }
  }, [fetchOnLoad]);

  // sample Sheduled meetings for meetings calender
  const fetchDetailsByDate = async (date) => {
    try {
      const formattedDate = dayjs(date).format("YYYY-MM-DD"); // Format selected date
      const result = await externalgetApi(Schedule_API_1, "get", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        SCHEDULEDATE: formattedDate,
        STS: CON_STATUS_ACTIVE,
      });

      const dates = result?.Table1?.map((item) => item.SCHEDULEDATE) || [];
      setScheduledDates(dates);
      // Assuming the API returns Table1 and Table2
      if (result?.Table2?.length > 0) {
        const details = result.Table2.map(
          (item) =>
            `${item.GUESTNAME} | ${item.CONTACTNO} | ${item.EMAIL} | From: ${item.TIMEFROM} To: ${item.TIMETO}`
        ).join("\n");

        setMeetingDetails(details); // Format Table2 data into meeting details
      } else if (!fetchOnLoad) {
        // Only show this message if not during initial fetch
        setMeetingDetails(
          "There are no tasks scheduled for today. Focus on serving customers and maintaining a smooth operation!"
        );
      }
    } catch (err) {
      console.error("Error fetching details:", err);
    }
  };

  const handleInputValidation = (event) => {
    const { name, value } = event.target;
    const regex = /^[a-zA-Z\s]*$/;

    if (regex.test(value)) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      handleInputChange(event);
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Only letters and spaces are allowed.",
      }));
    }
  };

  const handleInputEmailValidation = (event) => {
    const { name, value } = event.target;
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    // Update userDetails state first
    handleInputChange(event);

    // Validate the email input
    if (value.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Email is required.", // Optional: check for empty input
      }));
    } else if (validateEmail(value)) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" })); // Clear error on valid email
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Please enter a valid email address.", // Set error message for invalid email
      }));
    }
  };

  const handleDayClick = (newValue) => {
    setMeetingDetails("");
    setSelectedDate(newValue);
    fetchDetailsByDate(newValue); // Call API with the selected date
    setOpen(true);
  };

  const handleNumberValidation = (event) => {
    const { name, value } = event.target;
    const regex = /^[0-9]*\.?[0-9]*$/;

    if (regex.test(value) && value.length <= 10) {
      // Valid input: matches regex and is within the 10-character limit
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      handleInputChange(event); // Update state with valid input
    } else if (value.length > 10) {
      // Invalid: Exceeds 10 characters
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Input cannot exceed 10 characters.",
      }));
    } else {
      // Invalid: Does not match regex
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Only numbers and floats are allowed.",
      }));
    }
  };

  return (
    <div>
      <Navbar />

      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs}
                position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">View Schedules</Link>
                <Link underline="none">Add Schedule</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                sx={{
                  ...styles.AI_fixedStack2,
                  direction: "row",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  onClick={gotToView}
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.text.buttontxt,
                    textTransform: "capitalize",
                    letterSpacing: "1px",
                    width: "80px",
                  }}
                >
                  Back
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.box_container_add}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={styles.col_title}>
              <Typography variant="title" align="left">
                Schedules
              </Typography>
            </Box>
          </Grid>
          {/* Form Section */}
          <Grid item xs={12} sm={12} md={5}>
            <Box p={1} sx={styles.st_page_table}>
              <Grid
                container
                spacing={2}
                sx={{
                  height: { xs: "auto", sm: "auto", md: "65vh" },
                }}
              >
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Typography
                    sx={{
                      ...theme.typography.h2,
                      color: theme.palette.text.primary,
                      py: 1,
                    }}
                  >
                    Add Schedules
                  </Typography>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={
                        <span>
                          Scheduled Date <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      onChange={(newValue) => {
                        setSelectedItemDetails((prevDetails) => ({
                          ...prevDetails,
                          SCHEDULEDATE: newValue,
                        }));

                        const formattedDate = newValue?.format("MM/DD/YYYY");

                        if (errors?.SCHEDULEDATE) {
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            SCHEDULEDATE: "",
                          }));
                        }

                        console.log(formattedDate);
                      }}
                      value={
                        selectedItemDetails.SCHEDULEDATE
                          ? dayjs(selectedItemDetails.SCHEDULEDATE)
                          : null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          error={Boolean(errors.SCHEDULEDATE)}
                          helperText={errors.SCHEDULEDATE}
                        />
                      )}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: Boolean(errors.SCHEDULEDATE), // Pass error here
                          helperText: errors.SCHEDULEDATE, // Pass helperText here

                          sx: {
                            width: "100%",
                            "& .MuiInputBase-root": {
                              // height: "40px",
                              // marginTop: "0.3rem",
                            },
                            "& .MuiInputAdornment-root .MuiIconButton-root": {
                              color: theme.palette.primary.main, // Change icon color here
                            },
                          },
                        },
                        popper: {
                          sx: {
                            "& .MuiPaper-root": {
                              backgroundColor: theme.palette.background.paper, // Background color of the popper
                              boxShadow: "none", // Remove box shadow if needed
                            },
                          },
                        },
                      }}
                      minDate={dayjs()}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Guest Name"
                    name="GUESTNAME"
                    value={selectedItemDetails.GUESTNAME}
                    onChange={handleInputValidation}
                    error={Boolean(errors.GUESTNAME)}
                    helperText={errors.GUESTNAME}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Contact Number"
                    name="CONTACTNO"
                    value={selectedItemDetails.CONTACTNO}
                    onChange={handleNumberValidation}
                    error={Boolean(errors.CONTACTNO)}
                    helperText={errors.CONTACTNO}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    name="EMAIL"
                    value={selectedItemDetails.EMAIL}
                    onChange={handleInputEmailValidation}
                    error={Boolean(errors.EMAIL)}
                    helperText={errors.EMAIL}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12} mt={-1}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["TimePicker", "TimePicker"]}>
                      <Grid item xs={12}>
                        <TimePicker
                          // label="Start Time "
                          label={
                            <span>
                              Start Time <span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          value={
                            selectedItemDetails.TIMEFROM
                              ? dayjs(selectedItemDetails.TIMEFROM)
                              : null
                          }
                          onChange={(newValue) => {
                            setSelectedItemDetails((prevDetails) => ({
                              ...prevDetails,
                              TIMEFROM: newValue,
                            }));

                            if (errors?.TIMEFROM) {
                              setErrors((prevErrors) => ({
                                ...prevErrors,
                                TIMEFROM: "",
                              }));
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              error: Boolean(errors.TIMEFROM), // Pass error here
                              helperText: errors.TIMEFROM, // Pass helperText here
                              sx: {
                                width: "100%",

                                "& .MuiInputBase-root": {
                                  // height: "40px",
                                  // maxHeight: "40px",
                                  overflow: "hidden",
                                  // marginTop: "0.4rem",
                                },
                                "& .MuiInputAdornment-root .MuiIconButton-root":
                                  {
                                    color: theme.palette.primary.main, // Change icon color here
                                  },
                                "& .MuiFormControl-root": {
                                  height: "40px", // Ensures that the FormControl container aligns with the input height
                                },
                              },
                            },
                            popper: {
                              sx: {
                                "& .MuiPaper-root": {
                                  backgroundColor:
                                    theme.palette.background.paper, // Background color of the popper
                                  boxShadow: "none", // Remove box shadow if needed
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TimePicker
                          // label="End Time"
                          label={
                            <span>
                              End Time <span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          onChange={(newValue) => {
                            setSelectedItemDetails((prevDetails) => ({
                              ...prevDetails,
                              TIMETO: newValue,
                            }));

                            if (errors?.TIMETO) {
                              setErrors((prevErrors) => ({
                                ...prevErrors,
                                TIMETO: "",
                              }));
                            }
                          }}
                          value={
                            selectedItemDetails.TIMETO
                              ? dayjs(selectedItemDetails.TIMETO)
                              : null
                          }
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              error: Boolean(errors.TIMETO), // Pass error here
                              helperText: errors.TIMETO, // Pass helperText here
                              sx: {
                                width: "100%",

                                "& .MuiInputBase-root": {
                                  // height: "40px",
                                  // maxHeight: "40px",
                                  overflow: "hidden",
                                  // marginTop: "0.4rem",
                                },
                                "& .MuiInputAdornment-root .MuiIconButton-root":
                                  {
                                    color: theme.palette.primary.main, // Change icon color here
                                  },
                                "& .MuiFormControl-root": {
                                  height: "40px", // Ensures that the FormControl container aligns with the input height
                                },
                              },
                            },
                            popper: {
                              sx: {
                                "& .MuiPaper-root": {
                                  backgroundColor:
                                    theme.palette.background.paper, // Background color of the popper
                                  boxShadow: "none", // Remove box shadow if needed
                                },
                                "& .MuiFormControl-root": {
                                  height: "40px", // Ensures that the FormControl container aligns with the input height
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                    </DemoContainer>
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Remark"
                    name="REMARK"
                    value={selectedItemDetails.REMARK}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={2}
                    m={1}
                    justifyContent="flex-end"
                    padding={1}
                  >
                    <Button
                      variant="outlined"
                      sx={styles.AI_button1}
                      onClick={handleClearClick}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      sx={styles.AI_button2}
                      onClick={handleConfirmClick}
                    >
                      {isEdit ? "Modify" : "Confirm"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                // ...styles.col_1,
                display: "flex",
                flexDirection: "column",
                // alignItems: "flex-end",
                justifyContent: "flex-start",
                padding: { xs: 0, sm: 0, md: 2 },
                height: "65vh",
                borderRadius: "4px",
                boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
                backgroundColor: theme.palette.background.paper,
                borderRight: theme.palette.divider,
                overflow: "hidden", // Prevent overflow
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Typography
                    sx={{
                      ...theme.typography.h2,
                      color: theme.palette.text.primary,
                      // pl: 1,
                      pb: 1,
                      padding: { xs: 1, sm: 1, md: 0 },
                    }}
                  >
                    Schedule Calendar
                  </Typography>
                  <Divider />
                </Grid>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      mt: 2,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <DateCalendar
                      defaultValue={dayjs()}
                      onChange={handleDayClick}
                      renderLoading={() => <DayCalendarSkeleton />}
                      sx={{
                        width: "100%", // Fit within the Box
                        maxWidth: "600px", // Set a reasonable max width
                        height: "auto", // Adjust height based on content
                        maxHeight: "150%", // Prevent the calendar from overflowing the container
                        backgroundColor: theme.palette.background.paper,
                        border: "2px solid #ccc",

                        borderRadius: "4px",
                        boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
                        overflow: "hidden", // Ensure no content spills outside

                        "& .MuiTypography-root": {
                          fontSize: "1.2rem",
                          margin: "4px",
                          color: theme.palette.primary.main,
                        },
                        "& .MuiPickersDay-root": {
                          fontSize: "1rem",
                          margin: "4px", // Keep margin small to avoid overflow
                          padding: "4px",
                          color: theme.palette.text.primary,
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          margin: "5px",
                          borderRadius: "50%",
                          cursor: "pointer",
                        },
                      }}
                      renderDay={(day, selectedDate, DayComponent) => {
                        const isScheduled = scheduledDates.includes(
                          dayjs(day).format("YYYY-MM-DD")
                        );
                        return (
                          <DayComponent
                            {...{
                              day,
                              selectedDate,
                              className: isScheduled ? "highlighted" : "",
                            }}
                          />
                        );
                      }}
                      slots={{
                        day: ServerDay,
                      }}
                      slotProps={{
                        day: {
                          highlightedDays: scheduledDates,
                        },
                      }}
                    />
                    {/* Dialog to display meeting details */}
                    <Dialog
                      open={open}
                      onClose={() => setOpen(false)}
                      fullWidth
                      maxWidth="sm"
                    >
                      <DialogTitle
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "1.5rem",
                        }}
                      >
                        Schedule Details
                      </DialogTitle>
                      <DialogContent
                        sx={{
                          bgcolor: "background.paper",
                          p: 3,
                          borderRadius: "8px",
                        }}
                      >
                        {/* Date Display */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ fontWeight: "bold" }}
                          >
                            Selected Date
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {selectedDate
                              ? dayjs(selectedDate).format("YYYY-MM-DD")
                              : "No date selected"}
                          </Typography>
                        </Box>

                        {/* Divider */}
                        <Divider sx={{ marginY: 2 }} />

                        {/* Meeting Details */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ fontWeight: "bold" }}
                          >
                            Scheduling information:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-line",
                              fontSize: "0.9rem",
                              color: "text.primary",
                            }}
                          >
                            {meetingDetails || "No meeting details available."}
                          </Typography>
                        </Box>
                      </DialogContent>
                    </Dialog>
                  </Box>
                </LocalizationProvider>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          // Slide in from the right
          sx={{ top: "10%", right: "10%" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={6000}
          onClose={handleErrorSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ top: "10%", right: "10%" }}
        >
          <Alert
            onClose={handleErrorSnackbarClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
}

export default Schedules;
